import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import { Thread } from "@/data/schemas";

export interface ThreadSlice {
  // State
  threadMap: Record<string, Thread>;

  // Actions (primary - use these)
  setThread: (thread: Thread) => void;
  setThreads: (threads: Thread[]) => void;
  updateThread: (threadKey: string, updates: Partial<Thread>) => void;
  removeThread: (threadKey: string) => void;
  clearThreads: () => void;

  // Getters (computed values - use as functions, not hooks)
  getThreadsList: () => Thread[];
  getThread: (threadKey: string) => Thread | undefined;

  // Backward compatibility aliases (deprecated - use Thread versions)
  /** @deprecated Use setThread instead */
  setGroup: (group: Thread) => void;
  /** @deprecated Use setThreads instead */
  setGroups: (groups: Thread[]) => void;
  /** @deprecated Use updateThread instead */
  updateGroup: (groupId: string, updates: Partial<Thread>) => void;
  /** @deprecated Use removeThread instead */
  removeGroup: (groupId: string) => void;
  /** @deprecated Use clearThreads instead */
  clearGroups: () => void;
  /** @deprecated Use getThreadsList instead */
  getGroupsList: () => Thread[];
  /** @deprecated Use getThread instead */
  getGroup: (groupId: string) => Thread | undefined;
}

export const createThreadSlice: StateCreator<AppStore, unknown, unknown, ThreadSlice> = (set, get) => ({
  // Initial state
  threadMap: {},

  // Actions
  setThread: (thread) =>
    set((state) => {
      console.log("[ThreadSlice] setThread called:", { id: thread.id, name: thread.name, members: thread.members });
      state.threadMap[thread.id] = thread;
      console.log("[ThreadSlice] threadMap now has", Object.keys(state.threadMap).length, "entries");
    }),

  setThreads: (threads) =>
    set((state) => {
      threads.forEach((t) => {
        state.threadMap[t.id] = t;
      });
    }),

  updateThread: (threadKey, updates) =>
    set((state) => {
      if (state.threadMap[threadKey]) {
        Object.assign(state.threadMap[threadKey], updates);
      }
    }),

  removeThread: (threadKey) =>
    set((state) => {
      delete state.threadMap[threadKey];
    }),

  clearThreads: () =>
    set((state) => {
      state.threadMap = {};
    }),

  // Getters
  getThreadsList: () => {
    const state = get();
    const environment = state.environment; // from hilo slice
    return Object.values(state.threadMap).map((thread) => {
      // Apply hilo environment logic
      if (environment === "hi") {
        return {
          ...thread,
          lastMessage: thread.hiloMeta?.lastMessage ?? thread.lastMessage,
          updatedAt: thread.hiloMeta?.lastMessageAt ?? thread.updatedAt,
        };
      }
      return thread;
    });
  },

  getThread: (threadKey) => get().threadMap[threadKey],

  // Backward compatibility aliases (these just delegate to the new names)
  setGroup: (group) => get().setThread(group),
  setGroups: (groups) => get().setThreads(groups),
  updateGroup: (groupId, updates) => get().updateThread(groupId, updates),
  removeGroup: (groupId) => get().removeThread(groupId),
  clearGroups: () => get().clearThreads(),
  getGroupsList: () => get().getThreadsList(),
  getGroup: (groupId) => get().getThread(groupId),
});
